import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  Book24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getGlossaryTermById,
  getGlossaryTerms,
  type GlossaryTerm as ApiGlossaryTerm,
} from "../../glossario/glossarioApi";

interface TermoView {
  id: number;
  abreviatura: string;
  designacao?: string | null;
  categoria?: string | null;
  sinonimos?: string | null;
}

const formatOptional = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value.trim() ? value : "—";
  return String(value);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar o glossário.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o glossário.";
};

const mapTermToView = (term: ApiGlossaryTerm): TermoView => {
  return {
    id: term.id,
    abreviatura: term.abbreviation,
    designacao: term.fullName ?? null,
    categoria: term.category ?? null,
    sinonimos: term.synonyms ?? null,
  };
};

export function GlossarioView() {
  const navigate = useNavigate();
  const [termos, setTermos] = useState<TermoView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTermId, setSelectedTermId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadTerms = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getGlossaryTerms();
        if (!isMounted) return;

        const mapped = response.map(mapTermToView);
        setTermos(mapped);
        setSelectedTermId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setTermos([]);
        setSelectedTermId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTerms();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredTermos = useMemo(
    () =>
      termos.filter((termo) => {
        const matchesSearch =
          termo.abreviatura
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (termo.designacao ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        return matchesSearch;
      }),
    [termos, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredTermos.length === 0) {
      setSelectedTermId(null);
      return;
    }

    const hasSelection = filteredTermos.some(
      (entry) => entry.id === selectedTermId,
    );
    if (!hasSelection) {
      setSelectedTermId(filteredTermos[0].id);
    }
  }, [filteredTermos, isLoading, selectedTermId]);

  useEffect(() => {
    if (!selectedTermId) return;

    let isMounted = true;

    const loadSelectedTerm = async () => {
      try {
        const response = await getGlossaryTermById(selectedTermId);
        if (!isMounted) return;

        const mapped = mapTermToView(response);
        setTermos((current) =>
          current.map((termo) =>
            termo.id === selectedTermId ? mapped : termo,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedTerm();

    return () => {
      isMounted = false;
    };
  }, [selectedTermId]);

  const selectedTermo = filteredTermos.find(
    (t) => t.id === selectedTermId,
  );

  const handleTermSelect = (termId: number) => {
    setSelectedTermId(termId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/glossario/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Termo
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Glossário"
        subtitle="Dicionário de termos e abreviaturas AJA2"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar termos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar termos..."
                : `${filteredTermos.length} termo${
                    filteredTermos.length !== 1 ? "s" : ""
                  } encontrado${
                    filteredTermos.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Book24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar termos...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Book24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredTermos.map((termo) => (
                <button
                  key={termo.id}
                  onClick={() => handleTermSelect(termo.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedTermId === termo.id ? "bg-accent/50" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedTermId === termo.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedTermId !== termo.id) {
                      e.currentTarget.style.backgroundColor = "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedTermId !== termo.id) {
                      e.currentTarget.style.backgroundColor = "var(--card)";
                    }
                  }}
                >
                  {selectedTermId === termo.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {termo.abreviatura}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(termo.designacao)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(termo.categoria)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading && !loadError && filteredTermos.length === 0 && (
              <div className="p-8 text-center text-secondary-text">
                <p>Nenhum termo encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedTermo ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Book24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um termo para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedTermo.abreviatura}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() =>
                        navigate(`/glossario/editar/${selectedTermo.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Termo</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Termo
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Designação Completa
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedTermo.designacao)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Categoria
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedTermo.categoria)}</p>
                  </div>

                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Sinónimos
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedTermo.sinonimos)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
